import React, {FC} from "react";
import VidaSection from "components/@vida/vida-section";
import Divider from "../divider";
import Spacer from "../spacer";
import Note from "../note";

const SPAPlatformAndLaterWiringDiagrams: FC = () => {
  return <div>
    <VidaSection title="" id="spa-platform-and-later-wiring-diagrams">
      <Divider/>
      <h1>SPA Platform and Later Wiring Diagrams</h1>

      {/* Wiring Component Functions */}
      <Spacer/>
      <VidaSection title="Wiring Component Functions" id="wiring-component-functions">
        <p><strong>Component functions</strong></p>
        <p>By right-clicking a component in a wiring diagram, a drop-down menu containing the name and options for the
          selected component is shown.</p>
        <img
          src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image1.jpg"
          alt="Wiring Component Functions_image1"/>
        <p>Fig. 68: Component information drop-down menu</p>
        <p>Read more about each option in the subsections below.</p>
        <Note body={<p>The Relay table option is only available for relay components and the Fuse table option is only
          available for fuse box components.</p>}/>
        <ol>
          <li>
            <p><strong>Follow component</strong></p>
            <p>Follow a component to another wiring diagram by right-clicking the component and select Follow component.
              This opens a pop-up containing a list of wiring diagrams in which the component is used.
            </p>
            <img
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image2.jpg"
              alt="Wiring Component Functions_image2"/>
            <p>Fig. 69: The Follow component pop-up</p>
            <p>When you have followed a component to another wiring diagram, the component is highlighted in blue.</p>
            <Note body={<p>Pay attention to the selected function and what wiring diagram you are navigating to when
              following a component. It is possible to navigate to wiring diagrams that are not directly related
              to the selected function.</p>}/>
          </li>
          <li>
            <p><strong>Relay table</strong></p>
            <p>A relay table is accessed by right-clicking a relay component and selecting <strong>Relay table</strong>.
              This opens a pop-up containing a list of all relays valid for the current vehicle.</p>
            <img
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image3.jpg"
              alt="Wiring Component Functions_image3"/>
            <p>Fig. 70: The Relay table pop-up</p>
            <p>The list contains the relay number and all components that are controlled by the relay.</p>
          </li>
          <li>
            <p><strong>Fuse table</strong></p>
            <p>A fuse table is accessed by right-clicking a fuse box component and selecting <strong>Fuse table</strong>.
              This opens a pop-up containing a list of all fuses valid for the current vehicle.</p>
            <img
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image4.jpg"
              alt="Wiring Component Functions_image4"/>
            <p>Fig. 71: The Fuse table pop-up</p>
            <p>The list contains the fuse number, fuse ID, amperage of the fuse and what component the fuse provides
              power feed for. If applicable, you can also see any components the power feed goes via before reaching the
              component the fuse provides power feed for.</p>
          </li>
          <li>
            <p><strong>Signal information</strong></p>
            <p><strong>Signal information for component</strong></p>
            <p>Signal information for a component is accessed by right-clicking the component and selecting <strong>Signal
              information</strong>. This opens a pop-up containing a table with complete information about all
              connections to the component. The pins shown on the wiring diagram where the signal information was
              accessed from are highlighted in blue. Pins not shown in the wiring diagram are not highlighted.</p>
            <img
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image5.jpg"
              alt="Wiring Component Functions_image5"/>
            <p>Fig. 72: The Signal information for component pop-up</p>
            <p>The table shows connector, pin, signal ID, direction of the signal, nearest connection, pin of nearest
              connection, wire thickness and wire colour. By hovering the mouse over a row, the expected measurement
              value is shown. You can print the table by clicking the <img className="icon"
                                                                           src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image6.jpg"
                                                                           alt="Wiring Component Functions_image6"/> icon.
            </p>
          </li>
          <li>
            <p><strong>Component images</strong></p>
            <p>Images for a component is accessed by right-clicking the component and selecting <strong>Component
              images</strong>. This opens a pop-up containing a drop-down menu of relevant images.</p>
            <img
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Wiring%20Component%20Functions_image7.jpg"
              alt="Wiring Component Functions_image7"/>
            <p>Fig. 73: The Component images pop-up</p>
            <p>The following image types can be selected from the drop-down menu in the upper left-hand part of the
              window:</p>
            <ul>
              <li>Component images</li>
              <li>Location images</li>
              <li>Pin-out images</li>
              <li>Layout images (only available for relays and fuse boxes)</li>
            </ul>
            <p><strong>Zoom and pan</strong></p>
            <p>Zoom images in and out by using the <strong>+</strong> and <strong>-</strong> buttons in the zoom icon,
              placed in the upper left corner of the wiring diagrams. It is also possible to zoom by using the scroll
              wheel on your mouse. Pan images by clicking and dragging to desired position, using the mouse.</p>
          </li>
        </ol>
        <p><strong>You may also be interested in:</strong></p>
        <p>Diagnostics how-tos (VDS)</p>
        <p>Working with wiring diagrams</p>
      </VidaSection>

      {/* SPA Platform and later Wiring Diagrams */}
      <Spacer/>
      <VidaSection title="SPA Platform and later Wiring Diagrams"
                   id="spa-platform-and-later-wiring-diagrams-subsection">
        <p><strong>Wiring Diagrams</strong></p>
        <p>When selecting a component in the ranked list, the relevant wiring diagram, are displayed under the Wiring
          Diagrams tab. Once a component is selected, it is highlighted in the wiring diagram which makes it easy to
          track. For some components there is more than one wiring diagram available. In such case all available wiring
          diagrams are listed above the currently viewed wiring diagram.</p>
        <p>The wiring diagram content is automatically filtered based on the VIN of the current vehicle. Please note
          that a wiring diagram may look different depending on the configuration of the vehicle.</p>
        <img
          src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/SPA%20Platform%20and%20later%20Wiring%20Diagrams_image.jpg"
          alt="SPA Platform and later Wiring Diagrams_image"/>
        <p>Fig. 67: The Wiring Diagrams Tab</p>
      </VidaSection>

      {/* Finding the location of a fuse or relay */}
      <Spacer/>
      <VidaSection title="Finding the location of a fuse or relay"
                   id="finding-the-location-of-a-fuse-or-relay">
        <p><strong>Finding the location of a relay or fuse</strong></p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <ol>
          <li>Open the <strong>Planning &amp; Diagnostics</strong> tab and select <strong>Fault Tracing</strong> in the
            left-hand menu panel.
          </li>
          <li>Select a function in which the fuse box or relay box is included from the drop-down menu. <br/> {"->"} The
            ranked list in the middle panel is populated.
          </li>
          <li>Select the <strong>Wiring Diagrams</strong> tab. In the ranked list, select the fuse box or relay box that
            you are looking for. <br/> {"->"} A wiring diagram where the fuse box or relay box is included is opened.
          </li>
          <li>Right click the fuse box or relay box symbol in the wiring diagram and select <strong>Component
            images</strong>. <br/> {"->"} The Component images pop-up is shown.
          </li>
          <li>Select <strong>Layout image</strong> from the drop-down menu. <br/> {"->"} The layout image is shown with
            all fuses or relays for the selected fuse box or relay box.
          </li>
        </ol>
      </VidaSection>

      {/* Finding the Fuse for a component */}
      <Spacer/>
      <VidaSection title="Finding the Fuse for a component"
                   id="finding-the-fuse-for-a-component">
        <p><strong>Finding the fuse for a component</strong></p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <ol>
          <li>Open the <strong>Planning &amp; Diagnostics</strong> tab and select <strong>Fault Tracing</strong> in the
            left-hand menu panel.
          </li>
          <li>Select a function from the drop-down menu, preferably <strong>Electrical
            distribution</strong>.<br/>{"->"} The ranked list in the middle panel is populated.
          </li>
          <li>Select the <strong>Wiring Diagrams</strong> tab. In the ranked list, click the component that you want to
            find the fuse for.<br/>{"->"} A wiring diagram where the component is included is opened.
          </li>
          <li>Follow the circuit back to the fuse box and note the fuse number.<br/><br/>
            <Note body={<p>You can right-click the fuse box symbol in the wiring diagram and select <strong>Fuse
              table</strong> and find the same fuse in the list. In this way you can also find other components that are
              connected to the same fuse.</p>}/>
          </li>
        </ol>
      </VidaSection>

      {/* Finding information on the Components page */}
      <Spacer/>
      <VidaSection title="Finding information on the Components page"
                   id="finding-information-on-the-components-page">
        <p><strong>Finding information on the Components page</strong></p>
        <p>The Components page can be used to navigate in a non-guided mode to read and learn more about components in
          VDS protocol-based vehicles. In the following procedure you will see that it is possible to use the Components
          page to find information for purposes other than fault tracing. The procedure describes some unique features
          of the Components page and how the different reference information tabs can be used.</p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p><strong>Connect the vehicle</strong></p>
        <ol>
          <li>Start by connecting the vehicle to work with, via cable or wirelessly.<br/>{"->"} The
            Planning &amp; Diagnostics tab is opened by default and assigned its own tab as soon as the vehicle is
            connected.
          </li>
        </ol>
        <p><strong>Check DTCs</strong></p>
        <ol start={2}>
          <li>Select <strong>Components</strong> in the left-hand menu panel. <br/>{"->"} The Components page is opened.
          </li>
        </ol>
        <ol start={3}>
          <li>By default, the list in the middle panel is set to view control modules. This list contains all the
            control modules that exist in the vehicle. In the list you can select a control module to only view
            information for that specific control module. If you want information about a component in the vehicle that
            is not a control module, you can select the <strong>Other</strong> button to populate the list with other
            components.
          </li>
          <li>The <strong>DTCs</strong> tab in the right-hand panel shows information about all set DTCs in the vehicle.
            You can select one control module and thereby filter the set DTC in the vehicle.
            The <strong>Confirmed</strong> tab shows all DTCs that are confirmed or the confirmation is in progress. To
            view all possible DTCs for the vehicle that can be set, you can select a control module and then
            select <strong>All DTCs</strong>. This can be used for reference purposes.
          </li>
          <li>The list of confirmed DTCs are ranked using DTC details and only views the DTCs from a control module
            perspective.
          </li>
          <li>Select a control module.<br/>
            {"->"} The DTCs set by the control module are shown in bold font style on top of the list with confirmed
            DTCs in the right-hand panel. The other DTCs are displayed in regular font.
          </li>
        </ol>
        <p><strong>Select readout time</strong></p>
        <ol start={7}>
          <li>By default VIDA is showing the vehicle DTC data from the latest vehicle readout. The date and time for the
            latest readout is shown in the Source drop-down menu. If an older vehicle readout exists, it is possible to
            select that older readout in order to see what the vehicle{"'"}s DTC status was at that specific time.
          </li>
          <li>To see if any previous readouts exist, click the Source drop-down menu.</li>
          <li>If an older readout exists, view the DTCs status for that earlier readout by selecting a date from
            the <strong>Source</strong> drop-down menu.<br/><br/><Note
              body={<p>When you select a readout older than the latest one, the Source drop-down menu is marked in red.
                Keep in mind that this represents an old snapshot of the vehicle status. To see the current status you
                need to switch back to the latest readout in the Source drop-down menu.</p>}/><br/>{"->"} The DTCs are
            updated with those DTCs that were set at the readout that was performed at the selected date.
          </li>
        </ol>
        <ol start={10}>
          <li>In order to view the latest DTC data again, click the <strong>Source</strong> drop-down menu.</li>
          <li>Select the latest readout.<br/>{"->"} The DTCs from the latest readout is now shown again.</li>
        </ol>
        <p><strong>View all possible DTCs</strong></p>
        <ol start={12}>
          <li>Select <strong>All DTCs</strong>.<br/>{"->"} All possible fault codes that can exist in the selected
            control module are presented in the list. Fault code descriptions can be selected and viewed by clicking the
            document title. This list is a specific feature of the Components page.
          </li>
        </ol>
        <p><strong>Check wiring diagrams for a component</strong></p>
        <ol start={13}>
          <li>Select a component in the <strong>Other</strong> list in the middle panel.</li>
          <li>Since the list is long, use the filtering option by clicking the <img className="icon"
                                                                                    src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Finding%20inforamtion%20on%20the%20Components%20page_image.jpg"
                                                                                    alt="Finding inforamtion on the Components page_image"/> icon.
          </li>
          <li>Enter a search string in the Name field for the component that you are looking for.<br/>{"->"} The list is
            filtered on entered text string.
          </li>
        </ol>
        <ol start={16}>
          <li>Select the component. It should now be easy to find in the filtered list.</li>
          <li>Since we are now looking at components (not control modules), the <strong>All DTCs</strong> list is empty.
            Select the <strong>Wiring Diagrams</strong> tab.
          </li>
          <li>A component may be connected to many functions, hence a part of many wiring diagrams. Select what function
            to view in the <strong>Function</strong> drop-down menu.<br/>{"->"} The selected wiring diagram is shown.
            The previously selected component is highlighted in the wiring diagram.
          </li>
        </ol>
        <p><strong>Run parameters</strong></p>
        <ol start={19}>
          <li>To view parameters connected to the selected component, select the <strong>Parameters</strong> tab.</li>
          <li>Click on relevant control module in the ECUs list.</li>
          <li>Select the parameters of interest in the list of parameters under the <strong>Parameters</strong> tab to
            the right.
          </li>
          <li>You can also add parameters from other control modules.</li>
          <li>To run the total selection of parameters, go to the <strong>Selected</strong> tab.<br/>{"->"} Here you can
            see the total selection of parameters from different control modules.
          </li>
          <li>Click on the <strong>Start</strong> button to run the parameters.</li>
          <li>To view the result, click on the Graphical View button.</li>
        </ol>
      </VidaSection>

      {/* Finding components per fuse */}
      <Spacer/>
      <VidaSection title="Finding components per fuse"
                   id="finding-components-per-fuse">
        <p><strong>Finding components per fuse</strong></p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <ol>
          <li>Open the <strong>Planning &amp; Diagnostics</strong> tab and select <strong>Fault Tracing</strong> in the
            left-hand menu panel.
          </li>
          <li>Select a function from the drop-down menu, preferably <strong>Electrical distribution</strong>.</li>
          <li>Select the <strong>Wiring Diagrams</strong> tab. In the ranked list, click the fuse box you want to find
            the fuse for.<br/>{"->"} A wiring diagram where the fuse box is included is opened.
          </li>
          <li>Right click the fuse box symbol in the wiring diagram and select <strong>Fuse
            table</strong>.<br/>{"->"} Here you can see information about all fuses available in the fuse box. You can
            also see each<br/>component that is connected per fuse.
          </li>
        </ol>
      </VidaSection>

      {/* Fault Tracing a Vehicles Network */}
      <Spacer/>
      <VidaSection title="Fault Tracing a Vehicles Network"
                   id="fault-tracing-a-vehicles-network">
        <p><strong>Fault tracing a vehicle{"'"}s network</strong></p>
        <p>This procedure describes how to fault trace a VDS protocol-based vehicle{"'"}s network. The description
          includes
          information about how the information for a network in the vehicle is presented in the ranked list and how to
          work with the ranked list to find a solution.</p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p>The procedure is based on a case where a customer has been complaining that her vehicle, an XC90 2016 is
          behaving strangely. Some features in the vehicle do not always work properly. You are about to start fault
          tracing the vehicle that is in the workshop and connected to the VIDA workstation.</p>
        <p><strong>Connect the vehicle</strong></p>
        <ol>
          <li>Start by connecting the vehicle via cable or wirelessly.<br/>{"->"} The Planning &amp; Diagnostics tab is
            opened by default as soon as the vehicle is connected and assigned its own tab. A network fault is indicated
            by the <img className="icon"
                        src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Fault%20Tracing%20a%20Vehicles%20Network_image1.jpg"
                        alt="Fault Tracing a Vehicles Network_image1"/> icon next to the Network tab in the
            left-hand menu panel. A network fault can generate a lot of unclear faults and should therefore always be
            prioritized and remedied first.
          </li>
        </ol>
        <p><strong>Check the ranked network list</strong></p>
        <ol start={2}>
          <li>Select the <strong>Network</strong> tab in the left-hand menu panel.<br/>{"->"} The Network page is
            opened. All networks in the vehicle are shown in the ranked list in the middle panel. The <img
              className="icon"
              src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Fault%20Tracing%20a%20Vehicles%20Network_image2.jpg"
              alt="Fault Tracing a Vehicles Network_image2"/> icon. icon indicates a network that has
            components with set DTCs. Networks without this symbol does not have any DTCs. The list is ranked, meaning
            that the network that is most likely to have faults is presented at the top of the list.
          </li>
        </ol>
        <ol start={3}>
          <li>Select the network with a <img className="icon"
                                             src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Fault%20Tracing%20a%20Vehicles%20Network_image2.jpg"
                                             alt="Fault Tracing a Vehicles Network_image2"/> icon. icon and
            click the <img className="icon"
                           src="https://cdn.volvotechinfo.com/vida-basic-training/SPA%20Platform%20and%20Later%20Wiring%20Diagrams/Fault%20Tracing%20a%20Vehicles%20Network_image3.jpg"
                           alt="Fault Tracing a Vehicles Network_image3"/> icon. to see the components it
            consists of.<br/>{"->"} The selected network in the list is expanded and you can see all components it
            consists of.
          </li>
        </ol>
        <ol start={4}>
          <li>In this case the first action in the list to check is a wire. Since the wire is presented first in the
            list it is the most probable cause of the problem. The best way to find out more about the wire is by using
            the wiring diagram. Select the wire in the selected network{"'"}s ranked list.
          </li>
        </ol>
        <p><strong>Start fault tracing</strong></p>
        <ol start={5}>
          <li>To find out where the wire is located in the wiring diagram, click the <strong>Wiring
            Diagrams</strong> tab.<br/>{"->"} The wiring diagram is shown with the signal marked in bold. A dashed line
            in the wiring diagram indicates that the wire is most likely the cause of the problem.
          </li>
        </ol>
        <ol start={6}>
          <li>Right click the dashed line and select <strong>Signal information</strong>.<br/>{"->"} A pop-up is opened.
          </li>
        </ol>
        <ol start={7}>
          <li>Identify what the wire is connected to and how to measure the signal and then close the pop-up.</li>
          <li>Right click the wire (the dashed line) once again and select <strong>Cable harness
            image</strong>.<br/>{"->"} A pop-up is opened.
          </li>
        </ol>
        <ol start={9}>
          <li>Identify where in the vehicle the wire is located. The connector is marked in green. Measure the signal
            and do a visual inspection of the pin and the wire in the vehicle to identify the faults. Remedy if needed.
          </li>
          <li>If no fault was found, continue with the next component in the ranked list.</li>
        </ol>
      </VidaSection>

      {/* Fault tracing a Vehicle using wiring diagrams */}
      <Spacer/>
      <VidaSection title="Fault tracing a Vehicle using wiring diagrams"
                   id="fault-tracing-a-vehicle-using-wiring-diagrams">
        <p><strong>Fault tracing a vehicle using wiring diagrams</strong></p>
        <p>This procedure describes how to fault trace a VDS protocol-based vehicle, using the ranked list and the
          wiring diagram. The procedure covers how you can use the wiring diagram to locate a faulty wire.</p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p>This procedure is based on a case where a customer has a problem with his vehicle. A function in the vehicle
          does not work properly. The vehicle is already connected to the VIDA workstation and DTCs have been read out.
          No CSC exists from earlier preplanning.</p>
        <p><strong>Add CSC to work list</strong></p>
        <ol>
          <li>Expand the <strong>Work List</strong> menu item in the left-hand menu panel and select <strong>Customer
            Symptom Codes</strong>.<br/>{"->"} The Customer Symptom Codes page with a list of CSC is opened.
          </li>
          <li>Type a search string in the search field for the CSC you need to add and press [Enter] on your
            keyboard.<br/>{"->"} The CSC matching the search string you entered is shown in the right-hand panel.
          </li>
        </ol>
        <ol start={3}>
          <li>Select CSC by using the checkboxes and click the <strong>Add to List</strong> button.<br/>{"->"} A pop-up
            is shown where you can enter a name and create a new work list.
          </li>
        </ol>
        <ol start={4}>
          <li>Enter a list name and click the <strong>Create</strong> button.<br/>{"->"} The work list is created and
            the CSC is added to the new work list.
          </li>
        </ol>
        <p><strong>Use the wiring diagram</strong></p>
        <ol start={5}>
          <li>In this case, the fault cause information for the component at the top of the ranked list gives you the
            information that a circuit is open. The most probable reason for this is that there is something wrong along
            the signal. To fault trace the open circuit, select the component at the top of the list and open
            the <strong>Wiring Diagrams</strong> tab.<br/>{"->"} The wiring diagram for the selected function is shown.
            The component at the top of the ranked list is selected and therefore marked in the wiring diagram. Hover
            the cursor over the wire to see the signal ID.
          </li>
          <li>To view the signal information, right click the wire and select <strong>Signal
            information</strong>.<br/>{"->"} A pop-up is shown.
          </li>
        </ol>
        <ol start={7}>
          <li>Find out which components the signal goes between and how to measure the signal and then close the
            pop-up.
          </li>
          <li>Right click the wire again and select <strong>Cable harness image</strong>.<br/>{"->"} A pop-up is shown.
          </li>
          <li>The endpoints for the wire are marked. You can click them to see images of the endpoints. Close the
            pop-up.
          </li>
          <li>Inspect the wire and connector visually in the vehicle. Measure it according to measurement information in
            the signal information. Replace the wire or connector if needed. If no replacement is needed, continue by
            fault tracing the next component in the ranked list.
          </li>
        </ol>
      </VidaSection>
    </VidaSection>
    <style jsx>{`
      img {
        width: 100%;
      }

      .icon {
        width: 25px;
      }
    `}</style>
  </div>
}

export default SPAPlatformAndLaterWiringDiagrams;
